import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart3,
  Target,
  TrendingUp,
  Database,
  Edit,
  Plus,
  Loader2,
  Info,
  ArrowRight
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

interface AutomationBenchmark {
  id: string;
  task_name: string;
  pillar: string;
  baseline_hours: number;
  automated_hours: number;
  savings_percentage: number;
  frequency: string;
}

interface PillarBenchmark {
  pillar: string;
  average_automation: number;
  industry_best: number;
  typical_range_min: number;
  typical_range_max: number;
}

export default function BenchmarksHubScreen() {
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch automation benchmarks
  const { data: automationBenchmarks = [], isLoading: automationLoading } = useQuery({
    queryKey: ['/api/admin/automation-benchmarks'],
    queryFn: () => apiRequest('/api/admin/automation-benchmarks')
  });

  // Fetch pillar benchmarks
  const { data: pillarBenchmarks = [], isLoading: pillarLoading } = useQuery({
    queryKey: ['/api/admin/pillar-benchmarks'],
    queryFn: () => apiRequest('/api/admin/pillar-benchmarks').catch(() => [])
  });

  const benchmarkAreas = [
    {
      title: "Automation Benchmarks",
      description: "Task-level automation data and time savings calculations",
      count: automationBenchmarks.length,
      icon: BarChart3,
      color: "bg-blue-500",
      tab: "automation",
      href: "/admin/benchmarks"
    },
    {
      title: "Pillar Benchmarks", 
      description: "Industry automation levels by business process pillar",
      count: pillarBenchmarks.length,
      icon: Target,
      color: "bg-green-500",
      tab: "pillars",
      href: "/admin/pillar-benchmarks"
    },
    {
      title: "Process Catalog",
      description: "Library of business processes and their automation potential",
      count: 45, // Placeholder - would come from API
      icon: Database,
      color: "bg-purple",
      tab: "processes",
      href: "/admin/process-catalog"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Benchmarks Data Hub</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage automation benchmarks, industry data, and process performance metrics
        </p>
      </div>

      {/* Benchmarks Guidelines */}
      <Card className="border-orange-200 bg-orange-50 dark:bg-orange-900/20">
        <CardHeader>
          <CardTitle className="flex items-center text-orange-800 dark:text-orange-200">
            <Info className="w-5 h-5 mr-2" />
            Benchmarks Data Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">Automation Benchmarks:</h4>
              <ul className="space-y-1 text-orange-700 dark:text-orange-300">
                <li>• Specific task-level automation data</li>
                <li>• Time savings and efficiency metrics</li>
                <li>• Baseline vs automated hours comparison</li>
                <li>• Used in ROI and value calculations</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">Pillar Benchmarks:</h4>
              <ul className="space-y-1 text-orange-700 dark:text-orange-300">
                <li>• High-level automation percentages by pillar</li>
                <li>• Industry average and best-in-class data</li>
                <li>• Used for maturity positioning</li>
                <li>• Provides strategic context for assessments</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2">Data Quality:</h4>
              <ul className="space-y-1 text-orange-700 dark:text-orange-300">
                <li>• Keep data current with industry trends</li>
                <li>• Validate automation percentages regularly</li>
                <li>• Ensure consistency across related benchmarks</li>
                <li>• Source data from reputable industry reports</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="automation">Automation Tasks</TabsTrigger>
          <TabsTrigger value="pillars">Pillar Benchmarks</TabsTrigger>
          <TabsTrigger value="processes">Process Catalog</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-3 gap-6">
            {benchmarkAreas.map((area) => {
              const Icon = area.icon;
              return (
                <Card key={area.tab} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${area.color} text-white`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{area.title}</CardTitle>
                        <CardDescription className="mt-1">
                          {area.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-4">
                      <div className="text-3xl font-bold text-gray-900 dark:text-white">
                        {area.count}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Data Points
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Button 
                        className="w-full" 
                        variant="outline"
                        onClick={() => setActiveTab(area.tab)}
                      >
                        <span>View {area.title}</span>
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      
                      <Link href={area.href}>
                        <Button className="w-full" variant="outline">
                          <Edit className="w-4 h-4 mr-2" />
                          Manage Data
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Benchmark Updates</CardTitle>
              <CardDescription>
                Latest changes to benchmark data and automation metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium">Automation benchmarks updated</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Added 3 new task automation metrics</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">2 days ago</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Target className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Pillar benchmarks refreshed</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Updated industry averages for Q4 2024</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">1 week ago</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="automation">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Automation Task Benchmarks</CardTitle>
                  <CardDescription>
                    Task-level automation data used in ROI calculations and assessments
                  </CardDescription>
                </div>
                <Link href="/admin/benchmarks">
                  <Button>
                    <Edit className="w-4 h-4 mr-2" />
                    Manage All
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {automationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading automation benchmarks...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task Name</TableHead>
                      <TableHead>Pillar</TableHead>
                      <TableHead>Baseline Hours</TableHead>
                      <TableHead>Automated Hours</TableHead>
                      <TableHead>Savings %</TableHead>
                      <TableHead>Frequency</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {automationBenchmarks.slice(0, 5).map((benchmark: AutomationBenchmark) => (
                      <TableRow key={benchmark.id}>
                        <TableCell className="font-medium">{benchmark.task_name}</TableCell>
                        <TableCell>{benchmark.pillar}</TableCell>
                        <TableCell>{benchmark.baseline_hours}h</TableCell>
                        <TableCell>{benchmark.automated_hours}h</TableCell>
                        <TableCell className="font-medium text-green-600">
                          {benchmark.savings_percentage}%
                        </TableCell>
                        <TableCell>{benchmark.frequency}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {automationBenchmarks.length > 5 && (
                <div className="mt-4 text-center">
                  <Link href="/admin/benchmarks">
                    <Button variant="outline">
                      View All {automationBenchmarks.length} Benchmarks
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pillars">
          <Card>
            <CardHeader>
              <CardTitle>Pillar Performance Benchmarks</CardTitle>
              <CardDescription>
                Industry automation levels and performance ranges by business process pillar
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pillarLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading pillar benchmarks...</span>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {pillarBenchmarks.map((pillar: PillarBenchmark) => (
                    <Card key={pillar.pillar} className="border">
                      <CardHeader>
                        <CardTitle className="text-lg capitalize">{pillar.pillar.replace('_', ' ')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Average Automation:</span>
                            <span className="font-medium">{pillar.average_automation}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Industry Best:</span>
                            <span className="font-medium text-green-600">{pillar.industry_best}%</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Typical Range:</span>
                            <span className="font-medium">{pillar.typical_range_min}% - {pillar.typical_range_max}%</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="processes">
          <Card>
            <CardHeader>
              <CardTitle>Process Catalog</CardTitle>
              <CardDescription>
                Comprehensive library of business processes and their automation characteristics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Process Catalog Management
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Manage the comprehensive library of business processes, their automation potential, and implementation guidance.
                </p>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Process
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}